import { useMemo } from "react";
import { StarIcon } from "@heroicons/react/24/solid";

import { clsxMerge } from "shared/lib/helpers";
import { PrimaryNavCampaignsSelector } from "@/components/navigation/primary/campaigns-selector";
import { Logo } from "@/components/navigation/shared/logo";
import { NavItemsRow } from "@/components/navigation/primary/navbar/nav-items-row";
import {
  NAV_V3_ITEMS,
  NAV_GROUP_NAMES,
} from "@/components/navigation/primary/constants";
import Router from "@/helpers/router";
import { useOnboarding } from "@/hooks/use-onboarding";
import { MobileDropdownMenu } from "@/components/navigation/primary/navbar/mobile-dropdown-menu";
import { NotificationsButton } from "@/components/navigation/shared/notifications-button";
import { PrimaryNavigationItemCollapsableWithIconI } from "@/components/navigation/primary/interfaces";
import { MessagesButton } from "../../shared/messages-button";
import { NavDropdown } from "./nav-items-row/components/nav-dropdown";
import { NavButtonPrimary } from "./nav-items-row/components/nav-button-primary";
import { isUserApprovedToCall } from "@/helpers/campaign";
import { useGlobalContext } from "@/hooks/use-global-context";
import { PRIMARY_NAVIGATION_V3_CONTAINER_ID } from "@/constants/element-ids";
import { TodayPerformanceButton } from "@/components/navigation/shared/today-performance-button";
import { APP_VERSION } from "@/constants/version";
import { SalesFloorNavButton } from "@/components/navigation/shared/sales-floor-button";
import { BugReportNavButton } from "@/components/navigation/shared/bug-report-button";
import { useMyWeekNavItems } from "@/components/navigation/primary/navbar/use-my-week-nav-items";
import { useTeamNavItems } from "@/components/navigation/primary/navbar/use-team-nav-items";
import { usePrimaryNavItems } from "@/components/navigation/primary/navbar/use-primary-nav-items";
import { useLeadsNavItem } from "@/components/navigation/primary/navbar/use-leads-nav-item";

import { NavigationBlockingBackground } from "@/components/navigation/primary/navbar/blocking-background";

/**
 * @returns the black navbar that goes along the top of the app UI.
 */
export const PrimaryNavigationV3NavBar = () => {
  const { campaigns } = useGlobalContext();
  const { step, isOnboarded } = useOnboarding();
  const myWeekNavItems = useMyWeekNavItems();
  // C-16 S1: Performance gains Rewards while its switch is on.
  const primaryNavItems = usePrimaryNavItems();
  // C-16 S6: Leads gains To-Dos while its switch is on.
  const leadsNavItem = useLeadsNavItem();
  // C-14: empty unless the user holds a role and the switch is on. Rendered
  // outside the onboarding check: an LGA or Admin may never have onboarded.
  const teamNavItems = useTeamNavItems();

  const isApprovedToCall = useMemo(
    () => campaigns?.some((c) => isUserApprovedToCall(c.campaign_user_status)),
    [campaigns]
  );

  const ONBOARDING_NAV_ITEM: PrimaryNavigationItemCollapsableWithIconI =
    useMemo(
      () => ({
        text: (
          <div className="flex items-center justify-between">
            <span className="mr-7">Getting Started</span>
            <span className="ae-typography-detail1 badge rounded-[4px] border-none bg-[#F88167] text-white">
              {step} of 2
            </span>
          </div>
        ),
        icon: <StarIcon className="w-4" />,
        href: Router.onboarding(),
        isCollapsed: true,
        disabled: false,
      }),
      [step, isOnboarded]
    );

  const settingsNavItems: PrimaryNavigationItemCollapsableWithIconI[] =
    useMemo(() => {
      if (!isOnboarded) {
        return [NAV_V3_ITEMS[NAV_GROUP_NAMES.ONBOARDING]];
      }

      return [NAV_V3_ITEMS[NAV_GROUP_NAMES.SETTINGS]];
    }, [step, isOnboarded, ONBOARDING_NAV_ITEM]);

  return (
    <div
      id={PRIMARY_NAVIGATION_V3_CONTAINER_ID}
      className={clsxMerge(
        "navbar z-30 justify-between bg-black p-0",
        "w-screen min-w-[800px]"
      )}
    >
      <NavigationBlockingBackground />

      <div className="navbar-start h-full">
        <div className="shrink-0 pl-10">
          <Logo variation="small_white" width="16px" />
        </div>

        {isOnboarded ? (
          <>
            <PrimaryNavCampaignsSelector className="px-7" />

            <NavButtonPrimary
              navItem={NAV_V3_ITEMS[NAV_GROUP_NAMES.HOME]}
              containerClassName="mr-1 xl:mr-2"
            />

            {myWeekNavItems.map((navItem) => (
              <NavButtonPrimary
                key={navItem.href}
                navItem={navItem}
                // Without shrink-0 the active pill overflowed its shrunk
                // container onto the Leads icon (seen at 1024-1280 px).
                containerClassName="mr-1 shrink-0 xl:mr-2"
                btnClassName="shrink-0"
              />
            ))}

            <NavDropdown
              navItem={leadsNavItem}
              dropdownClassName="mr-1 xl:mr-2"
              disabled={!isApprovedToCall}
            />

            <NavItemsRow data={primaryNavItems} hideOnResponsive />
          </>
        ) : (
          <NavItemsRow data={[ONBOARDING_NAV_ITEM]} className="ml-[50px]" />
        )}

        {teamNavItems.map((navItem) => (
          <NavDropdown
            key={navItem.id}
            navItem={navItem}
            dropdownClassName="mr-1 xl:mr-2"
          />
        ))}
      </div>

      <div className="navbar-end sticky right-0 z-20 h-full w-fit">
        {isOnboarded && (
          <div className="flex h-full items-center gap-1 xl:gap-2">
            <TodayPerformanceButton />
            <NotificationsButton />
            <MessagesButton />
            <SalesFloorNavButton />
          </div>
        )}

        {/* GLE-8483: shown only while the backend says bug reports are on. */}
        <div className="flex h-full items-center">
          <BugReportNavButton />
        </div>

        <NavItemsRow
          data={settingsNavItems}
          className="xl:mx-1"
          dropdownClassName="dropdown-end"
          dropdownFooter={
            <div className="w-full">
              <span className="relative -top-3 mb-2 ml-4 text-[#666] typography-body-5">
                GC VERSION {APP_VERSION}
              </span>
            </div>
          }
        />

        {/* VISIBLE ONLY ON WINDOW < 1024px WIDTH */}
        {isOnboarded && (
          <MobileDropdownMenu
            data={[
              ...myWeekNavItems,
              ...teamNavItems,
              ...primaryNavItems,
              NAV_V3_ITEMS[NAV_GROUP_NAMES.SETTINGS],
            ]}
          />
        )}

        {!isOnboarded && teamNavItems.length > 0 && (
          <MobileDropdownMenu data={teamNavItems} />
        )}
      </div>
    </div>
  );
};
